import { useRef, useCallback } from 'react';

export const useAudio = () => {
  const audioContextRef = useRef(null);
  const isInitializedRef = useRef(false);

  const initAudio = useCallback(async () => {
    if (isInitializedRef.current && audioContextRef.current) {
      return Promise.resolve();
    }

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        throw new Error('AudioContext not supported');
      }

      audioContextRef.current = new AudioContext();
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      isInitializedRef.current = true;
      console.log('Audio initialized successfully');
    } catch (error) {
      console.error('Error initializing audio:', error);
      isInitializedRef.current = false;
      audioContextRef.current = null;
    }
  }, []);

  const createSound = useCallback((frequency, duration) => {
    if (!audioContextRef.current || !isInitializedRef.current) {
      return;
    }

    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      const now = audioContextRef.current.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch (error) {
      console.error('Error creating sound:', error);
    }
  }, []);

  const playAccentSound = useCallback(() => {
    createSound(1000, 0.12);
  }, [createSound]);

  const playRegularSound = useCallback(() => {
    createSound(800, 0.08);
  }, [createSound]);

  const isInitialized = useCallback(() => {
    return isInitializedRef.current && audioContextRef.current?.state === 'running';
  }, []);

  return {
    initAudio,
    playAccentSound,
    playRegularSound,
    isInitialized
  };
};